/* BISD v2 global: cart API, cart drawer, menu, quick-add forms.
   The cart is authoritative: the drawer is re-rendered by Shopify (Section Rendering API) after
   every add or change, so totals, discounts and delivery progress are always the cart's own numbers. */
(() => {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const SECTION = 'bisd-cart-drawer';
  const reduce = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const drawer = () => document.querySelector('[data-drawer]');
  const GENERIC = 'Something went wrong. Please try again.';
  const STOCK = 'That quantity is not available right now.';
  const SLOW = 'This is taking too long. Please check your bag before trying again.';
  const TIMEOUT_MS = 15000;

  /* Dialogs (drawer + menu): native modal dialog for focus trap and Escape, with a slide transition. */
  function openDialog(dialog) {
    if (!dialog) return;
    if (dialog.closeTimer) {
      window.clearTimeout(dialog.closeTimer);
      dialog.closeTimer = 0;
      if (dialog.open) { dialog.classList.add('is-in'); return; }
    }
    if (dialog.open) return;
    dialog.showModal();
    document.documentElement.classList.add('v2-lock');
    if (reduce()) { dialog.classList.add('is-in'); return; }
    requestAnimationFrame(() => requestAnimationFrame(() => dialog.classList.add('is-in')));
  }
  function closeDialog(dialog) {
    if (!dialog || !dialog.open) return;
    dialog.classList.remove('is-in');
    window.clearTimeout(dialog.closeTimer);
    dialog.closeTimer = window.setTimeout(() => { dialog.closeTimer = 0; dialog.close(); }, reduce() ? 0 : 200);
  }
  function wireDialog(dialog) {
    if (!dialog || dialog.dataset.wired) return;
    dialog.dataset.wired = 'true';
    dialog.addEventListener('cancel', (event) => { event.preventDefault(); closeDialog(dialog); });
    dialog.addEventListener('close', () => {
      if (!document.querySelector('dialog[open]')) document.documentElement.classList.remove('v2-lock');
    });
    dialog.addEventListener('click', (event) => {
      if (event.target !== dialog) return;
      const r = dialog.getBoundingClientRect();
      const inside = event.clientX >= r.left && event.clientX <= r.right && event.clientY >= r.top && event.clientY <= r.bottom;
      if (!inside) closeDialog(dialog);
    });
  }

  /* Count badges */
  function setCount(count, bump) {
    document.querySelectorAll('[data-cart-count]').forEach((badge) => {
      badge.textContent = String(count);
      badge.hidden = count === 0;
      if (bump && !reduce()) {
        badge.classList.remove('is-bump');
        void badge.offsetWidth;
        badge.classList.add('is-bump');
      }
    });
    document.querySelectorAll('[data-cart-open]').forEach((link) => {
      link.setAttribute('aria-label', `Basket, ${count} ${count === 1 ? 'item' : 'items'}`);
    });
  }

  /* Swap the drawer contents for freshly rendered HTML; animate the delivery bar from its old fill. */
  function render(html, { bump = false } = {}) {
    const dialog = drawer();
    if (!dialog || !html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('[data-drawer-inner]');
    const current = dialog.querySelector('[data-drawer-inner]');
    if (!fresh || !current) return;
    const oldFill = current.querySelector('.v2-progress__fill');
    const oldP = oldFill ? getComputedStyle(oldFill).getPropertyValue('--p') : '0';
    const focusInside = dialog.contains(document.activeElement);
    const focusLine = focusInside && document.activeElement.dataset ? document.activeElement.dataset.change : undefined;
    const focusLabel = focusInside ? document.activeElement.getAttribute('aria-label') || '' : '';
    current.replaceWith(fresh);
    const newFill = fresh.querySelector('.v2-progress__fill');
    if (newFill && !reduce()) {
      const target = newFill.style.getPropertyValue('--p');
      newFill.style.setProperty('--p', oldP || '0');
      void newFill.offsetWidth;
      requestAnimationFrame(() => newFill.style.setProperty('--p', target));
    }
    setCount(Number(fresh.dataset.count || 0), bump);
    if (window.Shopify && window.Shopify.PaymentButton && typeof window.Shopify.PaymentButton.init === 'function') {
      try { window.Shopify.PaymentButton.init(); } catch (error) { /* express buttons are optional */ }
    }
    if (focusInside) {
      let target = null;
      if (focusLine) {
        target = Array.from(fresh.querySelectorAll('[data-change]')).find((n) => n.dataset.change === focusLine && (n.getAttribute('aria-label') || '') === focusLabel) || null;
      }
      (target || fresh.querySelector('[data-drawer-close]')).focus({ preventScroll: true });
    }
  }

  /* Every write gives up after 15s, so a stalled request can never leave the drawer locked. */
  async function post(path, body) {
    const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = ctrl ? window.setTimeout(() => ctrl.abort(), TIMEOUT_MS) : 0;
    let response;
    try {
      response = await fetch(`${root}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(Object.assign({}, body, { sections: SECTION, sections_url: window.location.pathname })),
        signal: ctrl ? ctrl.signal : undefined
      });
    } catch (fail) {
      window.clearTimeout(timer);
      refresh();
      const error = new Error(fail && fail.name === 'AbortError' ? SLOW : GENERIC);
      error.customer = true;
      throw error;
    }
    window.clearTimeout(timer);
    let data = {};
    try { data = await response.json(); } catch (error) { data = {}; }
    if (!response.ok) {
      await refresh();
      const error = new Error(response.status === 422 ? (data.description || STOCK) : GENERIC);
      error.customer = true;
      throw error;
    }
    return data;
  }
  /* Rendered drawer from a write response; falls back to a fresh fetch when sections are missing. */
  async function renderFrom(data, opts) {
    /* The /cart page shows the same basket: reload it so the page and the drawer always agree, but only
       once the queue is empty, so a second tap that is already queued is never lost to the reload. */
    if (document.querySelector('[data-cart-page]')) { reloadDue = true; return; }
    const html = data && data.sections && data.sections[SECTION];
    if (html) render(html, opts); else await refresh();
  }
  /* 15: every cart write goes through one queue, so taps can never interleave. */
  let queue = Promise.resolve();
  let pending = 0;
  let reloadDue = false;
  function enqueue(job) {
    pending += 1;
    const settle = () => {
      pending -= 1;
      if (!pending && reloadDue) window.location.reload();
    };
    const run = queue.then(job, job);
    run.then(settle, settle);
    queue = run.catch(() => {});
    return run;
  }
  /* Taps are blocked on the drawer and the /cart page while a write is in flight. */
  function busy(on) {
    [drawer(), document.querySelector('[data-cart-page]')].forEach((node) => { if (node) node.classList.toggle('is-busy', on); });
  }

  async function cartJson() {
    try {
      const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      return response.ok ? await response.json() : null;
    } catch (error) { return null; }
  }

  /* Adds items, re-renders the drawer. Opens it unless { open: false }. A failure after a successful add
     is never reported as "try again", so shoppers are not nudged into adding twice. */
  function add(items, { open = true } = {}) {
    return enqueue(async () => {
      busy(true);
      try {
        const data = await post('cart/add.js', { items });
        try { await renderFrom(data, { bump: true }); } catch (error) { /* added already */ }
        if (open) openDialog(drawer());
        return cartJson();
      } finally {
        busy(false);
      }
    });
  }

  /* ref: the line item key or a 1-based line number. Shopify re-keys a line when its discounts change
     (an add that crosses a tier), so the key captured at tap time is resolved against the live cart inside
     the queued job: same key first, else the same variant. A line that has gone just re-renders the drawer. */
  function change(ref, quantity) {
    return enqueue(async () => {
      busy(true);
      try {
        let body;
        if (/^\d+$/.test(String(ref))) {
          body = { line: Number(ref), quantity };
        } else {
          const c = await cartJson();
          if (c && Array.isArray(c.items)) {
            const variant = String(ref).split(':')[0];
            const line = c.items.find((i) => i.key === ref) || c.items.find((i) => String(i.variant_id) === variant);
            if (!line) { await refresh(); return; }
            body = { id: line.key, quantity };
          } else {
            body = { id: String(ref), quantity };
          }
        }
        const data = await post('cart/change.js', body);
        await renderFrom(data, { bump: quantity > 0 });
      } finally {
        busy(false);
      }
    });
  }

  async function refresh() {
    try {
      const response = await fetch(`${root}?sections=${SECTION}`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const data = await response.json();
      render(data[SECTION]);
    } catch (error) { /* keep current contents */ }
  }

  window.BISD = Object.assign(window.BISD || {}, { add, change, refresh, openDrawer: () => openDialog(drawer()), closeDrawer: () => closeDialog(drawer()), setCount });

  document.addEventListener('click', async (event) => {
    const opener = event.target.closest('[data-cart-open]');
    if (opener && drawer()) {
      event.preventDefault();
      openDialog(drawer());
      return;
    }
    if (event.target.closest('[data-drawer-close]')) { closeDialog(drawer()); return; }
    if (event.target.closest('[data-menu-open]')) { openDialog(document.querySelector('[data-menu]')); return; }
    if (event.target.closest('[data-menu-close]')) { closeDialog(document.querySelector('[data-menu]')); return; }
    const step = event.target.closest('[data-change]');
    if (step && !step.disabled) {
      if (step.getAttribute('aria-busy') === 'true') return;
      step.setAttribute('aria-busy', 'true');
      try {
        await change(step.dataset.change, Number(step.dataset.qty));
      } catch (error) {
        step.removeAttribute('aria-busy');
        const status = document.querySelector('[data-drawer-status]');
        if (status) status.textContent = error.customer ? error.message : GENERIC;
      }
    }
  });

  /* One-tap add forms (pairings, 2nd bottle). Without JS they post to /cart/add as normal forms. */
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-quick-add]');
    if (!form) return;
    event.preventDefault();
    const button = form.querySelector('button[type="submit"]');
    const select = form.querySelector('select[name="id"]');
    if (select && !select.value) { select.reportValidity(); select.focus(); return; }
    if (button.getAttribute('aria-busy') === 'true') return;
    /* Kit cards (e.g. Wash & mask routine) carry one [data-kit-id] per product and add them in one request. */
    const kitIds = Array.from(form.querySelectorAll('[data-kit-id]')).map((node) => Number(node.value)).filter(Boolean);
    const items = kitIds.length ? kitIds.map((id) => ({ id, quantity: 1 })) : [{ id: Number(form.querySelector('[name="id"]').value), quantity: 1 }];
    const inDrawer = Boolean(form.closest('[data-drawer]'));
    button.setAttribute('aria-busy', 'true');
    try {
      await add(items, { open: !inDrawer });
      if (!inDrawer) {
        button.classList.add('is-added');
        window.setTimeout(() => { button.classList.remove('is-added'); button.removeAttribute('aria-busy'); }, 1200);
      }
    } catch (error) {
      button.removeAttribute('aria-busy');
      const message = error && error.customer ? error.message : GENERIC;
      let status = inDrawer ? document.querySelector('[data-drawer] [data-drawer-status]') : form.querySelector('[data-quick-status]');
      if (!status) {
        status = document.createElement('p');
        status.className = 'v2-quick-status';
        status.setAttribute('role', 'status');
        status.dataset.quickStatus = '';
        form.appendChild(status);
      }
      status.textContent = message;
    }
  });

  /* ---------- Offer countdown: a fixed end date in UK time; removes itself at zero, never restarts. ---------- */
  const LONDON = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  function londonOffset(ms) {
    const parts = {};
    LONDON.formatToParts(new Date(ms)).forEach((p) => { parts[p.type] = Number(p.value); });
    return Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second) - Math.floor(ms / 1000) * 1000;
  }
  function parseLondon(text) {
    const m = String(text).trim().match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T ](\d{1,2}):(\d{2}))?/);
    if (!m) return NaN;
    const wall = Date.UTC(+m[1], +m[2] - 1, +m[3], m[4] ? +m[4] : 23, m[5] ? +m[5] : 59, 0);
    const first = wall - londonOffset(wall);
    return wall - londonOffset(first);
  }
  const pad = (n) => String(n).padStart(2, '0');
  /* When the 3+ offer ends: drop its UI, show the 2+ fallbacks, and let the PDP re-price. Runs once. */
  let t3Ended = false;
  function endTier3() {
    if (t3Ended) return;
    t3Ended = true;
    document.querySelectorAll('[data-t3-on]').forEach((node) => node.remove());
    document.querySelectorAll('[data-t3-off]').forEach((node) => { node.hidden = false; });
    document.querySelectorAll('.v2-offerbar__extra').forEach((node) => node.classList.remove('v2-offerbar__extra'));
    document.querySelectorAll('[data-t3-note]').forEach((node) => { node.textContent = node.dataset.t3Note; });
    document.dispatchEvent(new CustomEvent('bisd:t3end'));
    /* Cart-priced notes, the discount lines and the totals come from Shopify: re-render them. The /cart page
       reloads once (never in a loop, even if a cached copy still shows the countdown). */
    refresh();
    if (document.querySelector('[data-cart-page]')) {
      let again = false;
      try { again = window.sessionStorage.getItem('bisd-t3-reload') === '1'; window.sessionStorage.setItem('bisd-t3-reload', '1'); } catch (error) { again = true; }
      if (!again) enqueue(async () => { reloadDue = true; });
    }
  }
  function startCountdowns() {
    const items = Array.from(document.querySelectorAll('[data-countdown]')).filter((node) => !node.dataset.ticking).map((node) => { node.dataset.ticking = '1'; return node; }).map((node) => ({ node, end: parseLondon(node.dataset.countdown), label: node.dataset.label || 'Ends in' })).filter((item) => Number.isFinite(item.end));
    if (!items.length) return;
    let timer = 0;
    const tick = () => {
      const now = Date.now();
      let soon = false;
      items.forEach((item) => {
        if (!item.node.isConnected) return;
        const left = item.end - now;
        if (!(left > 0)) { item.node.remove(); endTier3(); return; }
        const s = Math.floor(left / 1000);
        const d = Math.floor(s / 86400);
        const h = Math.floor((s % 86400) / 3600);
        const m = Math.floor((s % 3600) / 60);
        const text = d > 0 ? `${d}d ${h}h ${m}m` : `${pad(h)}:${pad(m)}:${pad(s % 60)}`;
        if (d === 0) soon = true;
        const t = item.node.querySelector('[data-countdown-t]');
        const full = `${item.label} ${text}`;
        if (t && t.textContent !== full) t.textContent = full;
      });
      if (items.every((item) => !item.node.isConnected)) return;
      timer = window.setTimeout(tick, soon ? 1000 - (Date.now() % 1000) : 30000);
    };
    tick();
    document.addEventListener('visibilitychange', () => { if (!document.hidden) { window.clearTimeout(timer); tick(); } });
  }

  /* ---------- Reviews from other retailers: data-driven, hidden while there is no data. ----------
     data-match on each block: 'oxygenating' | 'oil control' | 'concealer' | '' (home: everything).
     Items whose product matches are shown as-is; otherwise ("brand" mode) the foundation reviews are shown
     under a brand heading with the brand wording, so nothing reads as a review of a different product. */
  const escHtml = (text) => String(text == null ? '' : text).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const matches = (product, key) => {
    const p = String(product || '').toLowerCase();
    if (!key || !p || p === 'all') return true;
    if (key === 'oxygenating') return p.includes('oxygenating') && !p.includes('oil control') && !p.includes('concealer');
    return p.includes(key);
  };
  /* Never round a rating up: floor to one decimal. */
  const floorRating = (r) => Math.floor(Number(r) * 10 + 1e-6) / 10;
  /* The label is shown as written when it is a full sentence ("Rated 4.3 out of 5 from ..."); otherwise it is
     built as "4.3 ★ · <count_label> <label>". Same wording on every page: it describes Oxygenetix as a brand. */
  function aggLine(a) {
    const r = floorRating(a.rating).toFixed(1);
    const label = String(a.label || 'ratings').trim();
    const star = '<span class="v2-star" aria-hidden="true">★</span>';
    if (/^rated\s/i.test(label)) return { html: `${star} ${escHtml(label)}`, aria: label };
    const count = a.count_label || Number(a.count).toLocaleString('en-GB');
    return { html: `${r} ${star} · ${escHtml(count)} ${escHtml(label)}`, aria: `Rated ${r} out of 5 from ${count} ${label}` };
  }
  function reviewDate(text) {
    const m = String(text || '').match(/^(\d{4})-(\d{2})/);
    if (!m) return escHtml(text || '');
    return new Intl.DateTimeFormat('en-GB', { month: 'short', year: 'numeric', timeZone: 'UTC' }).format(new Date(Date.UTC(+m[1], +m[2] - 1, 1)));
  }
  function cardHtml(item) {
    const source = item.url && /^https:\/\//.test(item.url)
      ? `<a href="${escHtml(item.url)}" target="_blank" rel="noopener nofollow">${escHtml(item.source)}<span class="visually-hidden"> (opens in a new tab)</span></a>`
      : escHtml(item.source);
    const when = item.date ? `, ${reviewDate(item.date)}` : '';
    const product = item.product && String(item.product).toLowerCase() !== 'all' ? `<span class="v2-rev__product">${escHtml(item.product)}</span>` : '';
    /* Stars only from the rating the reviewer actually gave on the source site; none when the data has no rating. */
    const n = Math.floor(Number(item.rating));
    const stars = n >= 1 && n <= 5 ? `<span class="v2-rev__stars" role="img" aria-label="${n} out of 5 stars">${'★'.repeat(n)}<span class="v2-rev__stars-off" aria-hidden="true">${'★'.repeat(5 - n)}</span></span>` : '';
    return `<li class="v2-rev"><figure class="v2-rev__fig">${stars}<blockquote class="v2-rev__q"><p>${escHtml(item.quote)}</p></blockquote><figcaption class="v2-rev__by"><strong>${escHtml(item.reviewer || 'Anonymous')}</strong>${product}<span class="v2-rev__src">${item.verified === true ? 'Verified review' : 'Review'} on ${source}${when}</span></figcaption></figure></li>`;
  }
  let reviewsData = null;
  function renderReviews() {
    if (!reviewsData) return;
    const aggs = [].concat(reviewsData.aggregate || [], reviewsData.aggregates || []).filter((a) => a && Number(a.rating) > 0 && Number(a.rating) <= 5 && Number(a.count) > 0);
    const items = (Array.isArray(reviewsData.items) ? reviewsData.items : []).filter((i) => i && i.quote && i.source);
    const listed = new Set();
    document.querySelectorAll('[data-reviews]').forEach((node) => {
      const key = node.dataset.match || '';
      const direct = items.filter((i) => matches(i.product, key));
      const brand = !direct.length && key !== '';
      let mine = (brand ? items : direct).slice();
      if (node.dataset.sort === 'short') mine.sort((x, y) => String(x.quote).length - String(y.quote).length);
      mine = mine.slice(0, Number(node.dataset.max) || 12);
      const agg = aggs.find((a) => matches(a.product, key)) || aggs[0];
      if (node.dataset.reviews === 'rating') {
        if (!agg) { node.hidden = true; return; }
        const line = aggLine(agg);
        /* The figure is for Oxygenetix as a brand; say so on every page except the Original foundation. */
        const brandNote = key !== 'oxygenating' ? 'Oxygenetix brand: ' : '';
        node.innerHTML = `<span>${brandNote ? `<span class="v2-rating__brand">${brandNote}</span>` : ''}${line.html}</span>`;
        if (document.getElementById('v2-reviews')) node.setAttribute('aria-label', `${brandNote}${line.aria}. Read reviews.`);
        else { node.removeAttribute('href'); node.setAttribute('role', 'note'); }
        node.hidden = false;
        return;
      }
      if (!mine.length && !agg) { node.hidden = true; return; }
      const title = node.querySelector('[data-reviews-title]');
      if (title) title.textContent = brand ? (node.dataset.brandTitle || title.textContent) : (node.dataset.heading || title.textContent);
      const aggBox = node.querySelector('[data-reviews-agg]');
      if (aggBox) { aggBox.hidden = !agg; if (agg) aggBox.innerHTML = `<span>${aggLine(agg).html}</span>`; }
      const list = node.querySelector('[data-reviews-list]');
      if (list) { list.hidden = !mine.length; list.innerHTML = mine.map(cardHtml).join(''); list.scrollLeft = 0; }
      node.hidden = false;
      listed.add(node);
    });
    return listed.size;
  }
  async function loadReviews() {
    const first = document.querySelector('[data-reviews]');
    if (!first) return;
    let data = null;
    const inline = document.getElementById('bisd-reviews-inline');
    if (inline) { try { data = JSON.parse(inline.textContent); } catch (error) { data = null; } }
    if (!data || typeof data !== 'object') {
      try {
        const response = await fetch(first.dataset.src, { headers: { Accept: 'application/json' } });
        data = response.ok ? await response.json() : null;
      } catch (error) { data = null; }
    }
    if (!data || typeof data !== 'object') return;
    reviewsData = data;
    renderReviews();
  }
  document.addEventListener('bisd:formula', (event) => {
    const key = event.detail && event.detail.f === 'c' ? 'oil control' : 'oxygenating';
    document.querySelectorAll('[data-reviews][data-formula-aware]').forEach((node) => { node.dataset.match = key; });
    renderReviews();
  });

  const start = () => {
    wireDialog(drawer());
    wireDialog(document.querySelector('[data-menu]'));
    startCountdowns();
    loadReviews();
  };
  Object.assign(window.BISD, { startCountdowns, loadReviews, renderReviews, parseLondon });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
  /* Back/forward cache can show a stale basket. */
  window.addEventListener('pageshow', (event) => { if (event.persisted) refresh(); });
})();
